/**
 * pagamentoStatus - deriva o "Status Financeiro" de um pedido a partir das parcelas.
 * A baixa é feita no Conta Azul e refletida em parcela.status via sync-back.
 */
import React from 'react';

const PAGO = ['pago', 'quitado', 'recebido', 'liquidado'];

const isPago = (p) => PAGO.includes((p?.status || '').toLowerCase());

const getVenc = (p) => p?.data_vencimento || p?.vencimento || null;

// Parse defensivo: 'YYYY-MM-DD' vira data LOCAL (evita off-by-one por UTC)
const parseVenc = (v) => {
  if (!v) return null;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [y, m, d] = v.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const dt = new Date(v);
  return isNaN(dt.getTime()) ? null : dt;
};

const isAtrasado = (p) => {
  if (isPago(p)) return false;
  const dt = parseVenc(getVenc(p));
  if (!dt) return false;
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  return dt < hoje;
};

const fmt = (n) => (n || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtData = (d) => {
  try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return ''; }
};

/** Retorna { key, label, color, resumo, pago, total, nPagas, nTotal, proxVenc } */
export function computePagamentoPedido(pedido) {
  const parcelas = pedido?.parcelas || [];
  const total = pedido?.total || 0;

  if (parcelas.length === 0) {
    return {
      key: 'sem_lancamento',
      label: 'Sem lançamento',
      color: 'gray',
      resumo: 'Parcelas ainda não lançadas no financeiro',
      pago: 0, total, nPagas: 0, nTotal: 0, proxVenc: null,
    };
  }

  const nTotal = parcelas.length;
  const nPagas = parcelas.filter(isPago).length;
  const pago = parcelas.reduce((s, p) => s + (isPago(p) ? (p.valor_pago ?? p.valor ?? 0) : 0), 0);
  const algumAtrasado = parcelas.some(isAtrasado);
  const pendentesComVenc = parcelas
    .filter(p => !isPago(p) && getVenc(p))
    .map(p => getVenc(p))
    .sort();
  const proxVenc = pendentesComVenc[0] || null;

  let key; let label; let color;
  if (nPagas === nTotal) {
    key = 'pago'; label = 'Pago'; color = 'green';
  } else if (algumAtrasado) {
    key = 'atrasado'; label = 'Atrasado'; color = 'red';
  } else if (nPagas === 0) {
    key = 'a_pagar'; label = 'A pagar'; color = 'amber';
  } else if (nPagas === 1 && isPago(parcelas[0])) {
    key = 'entrada_paga'; label = 'Entrada paga'; color = 'blue';
  } else {
    key = 'parcial'; label = `Parcial ${nPagas}/${nTotal}`; color = 'blue';
  }

  const partes = [`Pago ${fmt(pago)} de ${fmt(total)}`, `${nPagas}/${nTotal} parcelas`];
  if (proxVenc && key !== 'pago') partes.push(`próx. venc. ${fmtData(proxVenc)}`);

  return { key, label, color, resumo: partes.join(' · '), pago, total, nPagas, nTotal, proxVenc };
}

const COLOR_CLASSES = {
  gray: 'bg-gray-100 text-gray-600 border-gray-200',
  amber: 'bg-amber-100 text-amber-700 border-amber-300',
  blue: 'bg-blue-100 text-blue-700 border-blue-300',
  green: 'bg-green-100 text-green-700 border-green-300',
  red: 'bg-red-100 text-red-700 border-red-300',
};

/** Badge compacto de status financeiro. */
export function PagamentoBadge({ pedido, showResumo = false, className = '' }) {
  const st = computePagamentoPedido(pedido);
  return (
    <span
      className={`inline-flex items-center gap-1.5 ${className}`}
      data-testid={`pagamento-status-${pedido?.numero || pedido?.id}`}
      data-pagamento-key={st.key}
      title={st.resumo}
    >
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${COLOR_CLASSES[st.color]}`}>
        {st.color === 'green' ? '✓ ' : ''}{st.label}
      </span>
      {showResumo && (
        <span className="text-xs text-gray-500 hidden sm:inline">{st.resumo}</span>
      )}
    </span>
  );
}

export default PagamentoBadge;
